import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import api, { formatPrice, mediaUrl, track } from "../lib/api";

export default function SearchModal({ open, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef(null);

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 100); }, [open]);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/products", { params: { search: q, limit: 6 } });
        setResults(data.products);
        track("search", { query: q });
      } catch {} finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const go = (p) => { onClose(); setQ(""); navigate(`/product/${p.slug || p.id}`); };
  const submit = (e) => { e.preventDefault(); if (q.trim()) { onClose(); navigate(`/shop?search=${encodeURIComponent(q)}`); setQ(""); } };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-ivory border-linen max-w-2xl p-0 gap-0 top-[15%] translate-y-0" data-testid="search-modal">
        <DialogTitle className="sr-only">Search Products</DialogTitle>
        <form onSubmit={submit} className="flex items-center gap-3 px-6 py-5 border-b border-linen">
          <Search className="w-5 h-5 text-stonemuted" />
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} data-testid="search-input"
            placeholder="Search by brand, model, or SKU…"
            className="flex-1 bg-transparent outline-none font-serif text-xl placeholder:text-stonemuted/60" />
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-stonemuted" /></button>
        </form>
        <div className="max-h-[50vh] overflow-y-auto">
          {q && !loading && results.length === 0 && (
            <div className="px-6 py-12 text-center" data-testid="search-no-results">
              <p className="font-serif text-lg text-pitch">No pieces found</p>
              <p className="text-sm text-stonemuted mt-1">Try a different brand or keyword.</p>
            </div>
          )}
          {results.map((p) => (
            <button key={p.id} onClick={() => go(p)} data-testid="search-result-item"
              className="flex items-center gap-4 w-full px-6 py-3 hover:bg-bone transition-colors text-left">
              <div className="w-12 h-14 bg-bone shrink-0 overflow-hidden">
                {p.images?.[0] && <img src={mediaUrl(p.images[0])} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-mono uppercase tracking-widest text-stonemuted">{p.brand}</div>
                <div className="font-serif text-base text-pitch truncate">{p.title}</div>
              </div>
              <div className="text-sm">{formatPrice(p.price, p.currency)}</div>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
